/**
 * FairwayBid – wo die Anlage steht, ungefähr
 *
 * Für eine Tagesprognose braucht es keine Hausnummer. Eine Postleitzahl
 * reicht, und selbst davon die erste Ziffer: Die Leitregionen sind so
 * geschnitten, dass die erste Ziffer eine Ecke Deutschlands bezeichnet, und
 * das Wetter eines Tages ist über diese Entfernung selten ein anderes.
 *
 * Das ist bewusst so gebaut:
 *
 *   - Kein Geocoding-Dienst, kein zweiter Schlüssel, kein zweiter Vertrag.
 *   - Keine Adresse verlässt den Browser. Wir schicken Koordinaten einer
 *     Region an den Wetterdienst, nicht den Standort einer Anlage.
 *   - Fehlt die Postleitzahl, fehlt eben das Wetter. Eine Anlage nach ihrem
 *     Standort zu fragen, nur um ihr die Sonne anzuzeigen, wäre es nicht
 *     wert.
 *
 * Die Koordinaten sind die bekannten Zentren der Leitregionen. Genauer zu
 * werden hiesse, hundert Zahlenpaare zu pflegen, die niemand nachprüft.
 */

/** Erste PLZ-Ziffer → Mittelpunkt der Leitregion. */
export const PLZ_REGIONEN = {
  '0': { lat: 51.34, lon: 12.37, name: 'Sachsen und Umgebung' },
  '1': { lat: 52.52, lon: 13.4, name: 'Berlin und Brandenburg' },
  '2': { lat: 53.55, lon: 9.99, name: 'Hamburg und Umgebung' },
  '3': { lat: 52.37, lon: 9.73, name: 'Niedersachsen und Nordhessen' },
  '4': { lat: 51.51, lon: 7.47, name: 'Ruhrgebiet und Münsterland' },
  '5': { lat: 50.94, lon: 6.96, name: 'Köln, Bonn und Umgebung' },
  '6': { lat: 50.11, lon: 8.68, name: 'Rhein-Main und Saarland' },
  '7': { lat: 48.78, lon: 9.18, name: 'Baden-Württemberg' },
  '8': { lat: 48.14, lon: 11.58, name: 'München und Südbayern' },
  '9': { lat: 49.45, lon: 11.08, name: 'Franken und Oberpfalz' },
};

/**
 * Die beiden Bezugspunkte für die Händlersicht.
 *
 * Hamburg und München: weit genug auseinander, dass "Norden" und "Süden"
 * wirklich zwei Wetterlagen sind, und bekannt genug, dass niemand fragen
 * muss, was gemeint ist.
 */
export const NORD = { lat: 53.55, lon: 9.99, label: 'Norden' };
export const SUED = { lat: 48.14, lon: 11.58, label: 'Süden' };

/**
 * Wo könnte eine Postleitzahl am Nutzer stehen?
 *
 * Mehrere Schlüssel, weil das Feld in der Console angelegt wird und dort
 * heissen kann, wie es will. Lieber vier Namen prüfen, als eine Kachel
 * leer zu lassen, weil das Feld "postleitzahl" statt "plz" heisst.
 */
const PLZ_SCHLUESSEL = ['plz', 'postleitzahl', 'postal_code', 'postalCode', 'zip'];

const ausDaten = (daten, schluessel) => {
  if (!daten || typeof daten !== 'object') return null;
  for (const key of schluessel) {
    const wert = daten[key];
    if (typeof wert === 'string' && wert.trim()) return wert.trim();
    if (typeof wert === 'number') return String(wert);
  }
  return null;
};

/** Die Postleitzahl des angemeldeten Nutzers – aus public oder protected. */
export const plzVon = user => {
  const profil = user?.attributes?.profile || {};
  return (
    ausDaten(profil.publicData, PLZ_SCHLUESSEL) ||
    ausDaten(profil.protectedData, PLZ_SCHLUESSEL) ||
    ausDaten(profil.privateData, PLZ_SCHLUESSEL) ||
    null
  );
};

/** Die Region zu einer Postleitzahl – oder null, wenn sie keine ist. */
export const regionFuerPlz = plz => {
  const ziffern = String(plz || '').replace(/\D/g, '');
  if (ziffern.length < 4) return null;
  return PLZ_REGIONEN[ziffern[0]] || null;
};

/** Die Region des angemeldeten Nutzers, in einem Schritt. */
export const regionVon = user => regionFuerPlz(plzVon(user));
